import type {MatrixCall} from 'matrix-js-sdk/lib/webrtc/call';
import {CameraResumeError, type ReliefSnapshot, type VideoReliefAdapter, type VideoState} from './videoReliefController';

const ENDED_STATE = 'ended';
const CONNECTED_STATE = 'connected';

interface MatrixReliefDeps {
    readonly getCall: () => MatrixCall | null;
    readonly setCamera: (enabled: boolean) => Promise<unknown>;
}

const isHeld = (call: MatrixCall | null): boolean => !!call && typeof call.isRemoteOnHold === 'function' && call.isRemoteOnHold();

const alive = (call: MatrixCall | null): call is MatrixCall => !!call && call.state !== ENDED_STATE;

const remoteTracks = (call: MatrixCall): MediaStreamTrack[] => call.remoteUsermediaFeed?.stream?.getVideoTracks() ?? [];

const cameraIsOn = (call: MatrixCall): boolean => call.hasLocalUserMediaVideoTrack && !call.isLocalVideoMuted();

const withoutReceive = (direction: RTCRtpTransceiverDirection): RTCRtpTransceiverDirection =>
    direction === 'sendrecv' ? 'sendonly' : direction === 'recvonly' ? 'inactive' : direction;

const withReceive = (direction: RTCRtpTransceiverDirection): RTCRtpTransceiverDirection =>
    direction === 'sendonly' ? 'sendrecv' : direction === 'inactive' ? 'recvonly' : direction;

const receives = (direction: RTCRtpTransceiverDirection): boolean => direction === 'sendrecv' || direction === 'recvonly';

const isStopped = (transceiver: RTCRtpTransceiver): boolean =>
    (transceiver as {stopped?: boolean}).stopped === true || transceiver.currentDirection === 'stopped';

const canRenegotiate = (call: MatrixCall): boolean =>
    call.state === CONNECTED_STATE && !!call.peerConn && (call as unknown as {opponentVersion?: unknown}).opponentVersion !== 0;

const findTransceiver = (call: MatrixCall): RTCRtpTransceiver | undefined => {
    const track = remoteTracks(call)[0];
    return track ? call.peerConn?.getTransceivers().find(candidate => candidate.receiver.track === track) : undefined;
};

export const createMatrixReliefAdapter = ({getCall, setCamera}: MatrixReliefDeps): VideoReliefAdapter => {
    const disabled = new Set<MediaStreamTrack>();
    let transceiver: RTCRtpTransceiver | undefined;
    let noReceive = false;

    const setDirection = (target: RTCRtpTransceiver, direction: RTCRtpTransceiverDirection): void => {
        if (target.direction === direction || isStopped(target)) {
            return;
        }
        try {
            target.direction = direction;
        } catch {
            return;
        }
    };

    const disableNew = (call: MatrixCall): void => {
        remoteTracks(call).forEach(track => {
            if (track.enabled && !disabled.has(track)) {
                track.enabled = false;
                disabled.add(track);
            }
        });
    };

    const read = (): VideoState => {
        const call = getCall();
        if (!alive(call)) {
            return {cameraOn: false, remoteVideoOn: false};
        }
        const remoteMuted = call.remoteUsermediaFeed?.isVideoMuted() ?? true;
        return {
            cameraOn: cameraIsOn(call),
            remoteVideoOn: !noReceive && call.hasRemoteUserMediaVideoTrack && !remoteMuted && remoteTracks(call).some(track => track.enabled),
        };
    };

    const pause = async (): Promise<ReliefSnapshot> => {
        const call = getCall();
        if (!alive(call)) {
            throw new Error('Call is not active');
        }
        const cameraWasOn = cameraIsOn(call);
        if (cameraWasOn) {
            await setCamera(false);
            if (cameraIsOn(call)) {
                throw new Error('Camera did not turn off');
            }
        }
        const remoteVideoWasOn = !(call.remoteUsermediaFeed?.isVideoMuted() ?? true);
        const target = canRenegotiate(call) ? findTransceiver(call) : undefined;
        disableNew(call);
        if (target && !isStopped(target)) {
            transceiver = target;
            noReceive = true;
            setDirection(target, withoutReceive(target.direction));
        }
        return {cameraWasOn, remoteVideoWasOn: remoteVideoWasOn && (disabled.size > 0 || noReceive), mode: noReceive ? 'renegotiate' : 'local'};
    };

    const resume = async (snapshot: ReliefSnapshot): Promise<void> => {
        const call = getCall();
        if (!alive(call)) {
            return;
        }
        const held = isHeld(call);
        try {
            if (!held && snapshot.cameraWasOn && !cameraIsOn(call)) {
                await setCamera(true);
                if (!cameraIsOn(call)) {
                    throw new CameraResumeError('Camera did not turn on');
                }
            }
        } catch (error) {
            throw error instanceof CameraResumeError ? error : new CameraResumeError(error instanceof Error ? error.message : String(error));
        } finally {
            if (transceiver && !held && !isHeld(call)) {
                setDirection(transceiver, withReceive(transceiver.direction));
            }
            transceiver = undefined;
            noReceive = false;
            const release = [...disabled];
            release.forEach(track => {
                if (track.readyState !== 'ended') {
                    track.enabled = true;
                }
                disabled.delete(track);
            });
        }
    };

    const sync = (): void => {
        const call = getCall();
        if (!alive(call)) {
            return;
        }
        if (isHeld(call)) {
            return;
        }
        disableNew(call);
        if (noReceive && transceiver && !isStopped(transceiver) && receives(transceiver.direction)) {
            setDirection(transceiver, withoutReceive(transceiver.direction));
        }
    };

    return {read, pause, resume, sync, holdsRemote: () => noReceive || disabled.size > 0, isHeld: () => isHeld(getCall())};
};
